import { createElement } from "react";
import { BarChart01_1Icon } from "vega-icons";

import {
  type Article,
  ArticleSectionType,
  PreviewType,
} from "../types/article";

const chartColors = [
  "var(--data-1)",
  "var(--data-2)",
  "var(--data-3)",
  "var(--data-4)",
  "var(--data-5)",
  "color-mix(in srgb, var(--data-1) 60%, white)",
  "color-mix(in srgb, var(--data-2) 60%, white)",
  "color-mix(in srgb, var(--data-3) 60%, white)",
];

export const celestialArticles: Article[] = [
  {
    id: "celestial-1",
    slug: "getting-started-with-investments",
    title: "Deep Dive: Private Credit",
    description:
      "Learn the fundamentals of investment strategies and portfolio management tailored for Celestial partners.",
    previewUrl: "/images/resources/celestial/celestial-article-1.png",
    previewType: PreviewType.Image,
    author: "Celestial Corp.",
    publishedDate: "2025-11-14T16:12:00Z",
    readingTime: 6,
    published: true,
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Private Credit involves the financing of non-public companies or projects with the goal of earning interest income and potentially capital appreciation. Unlike traditional bank loans, Private Credit financing is provided by non-traditional providers such as credit funds. These financings are typically structured as loans or debt financings provided directly to companies, often without the oversight or ratings provided by traditional financial institutions.</p>",
        },
      },
      {
        id: "section-1",
        type: ArticleSectionType.TITLE,
        content: {
          text: "What are typical features of Private Credit?",
          level: 5,
        },
      },
      {
        id: "section-1-features",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 class="body-semi-large-semibold mb-4 text-text/primary">Structural features</h3>
              <p class="mb-3 text-text/secondary"><strong class="body-base-semibold text-text/secondary">Seniority:</strong> Private Credit are often senior loans, which sit at the top in a company's capital structure. They are repaid before junior debt and equity, providing downside risk protection</p>
              <p class="mb-3 text-text/secondary"><strong class="body-base-semibold text-text/secondary">Lender-friendly structures:</strong> Private Credit loans typically offer greater flexibility to negotiate lender-friendly terms</p>
              <p class="text-text/secondary"><strong class="body-base-semibold text-text/secondary">Buy and hold investment:</strong> many benefits of Private Credit are realized by holding the investment to maturity, therefore requiring patient long-term capital</p>
            </div>
            <div>
              <h3 class="body-semi-large-semibold mb-4 text-text/primary">Economic features</h3>
              <p class="mb-3 text-text/secondary"><strong class="body-base-semibold text-text/secondary">Floating rate coupons:</strong> coupon payments comprised of a base interest rate plus a spread. Floating coupons move with interest rates, mitigating interest rate and inflation risk</p>
              <p class="mb-3 text-text/secondary"><strong class="body-base-semibold text-text/secondary">Spread:</strong> compensates for illiquidity, complexity of financings, and other credit risks. Usually higher than in traditional financings</p>
              <p class="text-text/secondary"><strong class="body-base-semibold text-text/secondary">Discounted price:</strong> typically loans are issued at a price below par (e.g. below £1,000), which is payable at maturity. When held to maturity, this offers investors additional yield on top of the loan's cash coupons</p>
            </div>
          </div>`,
        },
      },
      {
        id: "section-2",
        type: ArticleSectionType.TITLE,
        content: {
          text: "How do Private Credit funds operate?",
          level: 5,
        },
      },
      {
        id: "section-2-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Private Credit funds lend to businesses that may not qualify for traditional bank loans. Funds often target mid-sized enterprises, offering them tailored debt solutions that can include senior debt, mezzanine financing, and other hybrid instruments. A Private Credit fund's main source of revenue is the interest received from loans. This interest income is generally higher than that received on traditional bank loans, given the increased risk associated with private lending transactions. In addition to interest, funds earn fees for originating loans. Types of Private Credit investments include direct lending, special situations / opportunistic credit, distressed debt, and mezzanine financing.</p>",
        },
      },
      {
        id: "section-2-2",
        type: ArticleSectionType.TABLE,
        content: {
          headers: ["Private Credit type", "Overview"],
          rows: [
            [
              "Direct lending",
              {
                type: "bullets",
                items: [
                  "Funds lend directly to companies, bypassing banks",
                  "Fixed repayment loans that are secured by the borrower's assets",
                ],
              },
            ],
            [
              "Special situations / Opportunistic credit",
              {
                type: "bullets",
                items: [
                  "Lending based on specific scenarios such as turnaround situations, debtor-in-possession financings, or bridge loans",
                ],
              },
            ],
            [
              "Distressed debt",
              {
                type: "bullets",
                items: [
                  "Acquisition of debt of companies that are in financial distress or bankruptcy",
                ],
              },
            ],
            [
              "Mezzanine financing",
              {
                type: "bullets",
                items: [
                  "Hybrid form of capital that is subordinated to senior lenders, but ranks above equity investors in the capital structure, granting it preference in principal recovery in case of borrower bankruptcy",
                  "Carries higher interest rates and may include equity components, such as warrants or options",
                ],
              },
            ],
          ],
        },
      },
      {
        id: "section-3",
        type: ArticleSectionType.TITLE,
        content: {
          text: "How does Private Credit generate returns?",
          level: 5,
        },
      },
      {
        id: "section-3-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Private Credit providers offer flexibility, speed, and specialization, allowing them to charge higher interest rates. Private Credit offers higher yields than public bonds, compensating for illiquidity and higher risk, and provides steady income streams.</p>",
        },
      },
      {
        id: "section-4",
        type: ArticleSectionType.TITLE,
        content: {
          text: "How does Private Credit fit into a portfolio?",
          level: 5,
        },
      },
      {
        id: "section-4-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Reallocating 10% of a traditional 60/40 portfolio to Private Credit would have reduced volatility and increased returns in the period September 2004 - June 2023 (graph below). This is because of Private Credit's low correlation with more traditional asset classes like stocks and bonds, hence providing diversification benefits. Private Credit's higher steady income streams offer protection against volatility and losses in times of market downturns, offering fixed income-like lower volatility, while also offering equity-like, higher returns.</p>",
        },
      },
      {
        id: "section-4-2",
        type: ArticleSectionType.SCATTER_CHART,
        content: {
          icon: createElement(BarChart01_1Icon),
          title: "Historical asset class income analysis (Sep 2004 – Jun 2023)",
          data: [
            {
              id: "private-credit",
              x: 4.5,
              y: 9.6,
              label: "Private Credit",
            },
            {
              id: "us-core-bonds",
              x: 4.0,
              y: 2.0,
              label: "US Core Bonds",
            },
            {
              id: "us-equities",
              x: 17.5,
              y: 9.6,
              label: "US Equities",
            },
            {
              id: "us-high-yield",
              x: 11.0,
              y: 5.6,
              label: "US High Yield",
            },
          ],
          xAxisLabel: "Annualized volatility",
          xAxisUnit: "%",
          yAxisUnit: "%",
          xDomain: [2, 20],
          yDomain: [0, 10.5],
          footerText: "Source: Oak Hill Advisors, T.RowePrice",
          height: 280,
        },
      },
      {
        id: "section-3-2",
        type: ArticleSectionType.SCATTER_CHART,
        content: {
          icon: createElement(BarChart01_1Icon),
          title:
            "Hypothetical portfolio risk / reward analysis (Sep 2004 – Jun 2023)",
          data: [
            {
              id: "private-credit-portfolio",
              x: 9.0,
              y: 7.9,
              label: [
                "55% Equities",
                "35% Fixed Income",
                "10% Private Credit Portfolio",
              ],
            },
            {
              id: "traditional-6040",
              x: 9.8,
              y: 7.7,
              label: ["60% Equities", "40% Fixed Income Portfolio"],
            },
          ],
          arrows: [
            {
              from: "traditional-6040",
              to: "private-credit-portfolio",
            },
          ],
          xAxisLabel: "Annualized volatility",
          xAxisUnit: "%",
          yAxisUnit: "%",
          xDomain: [8.5, 10.5],
          yDomain: [7.6, 8.0],
          footerText: "Source: Oak Hill Advisors, T.RowePrice",
          height: 260,
        },
      },
    ],
  },
  {
    id: "celestial-2",
    slug: "deep-dive-real-assets",
    title: "Deep Dive: Real Assets",
    description:
      "Comprehensive analysis of market trends and opportunities for the upcoming year.",
    previewUrl: "/images/resources/celestial/celestial-article-2.png",
    previewType: PreviewType.Image,
    author: "Michael Rodriguez",
    publishedDate: "2025-01-03T14:30:00Z",
    readingTime: 12,
    published: true,
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Real Asset investing consists of investing across a broad range of assets, such as real estate, infrastructure, and natural resources, including e.g. metals and commodities. These assets typically demonstrate resilience against market volatility and inflation, deriving their fundamental value from their physical properties, utility, scarcity, and demand.</p>",
        },
      },
      {
        id: "section-0",
        type: ArticleSectionType.TITLE,
        content: {
          text: "What are types of Real Assets?",
          level: 5,
        },
      },
      {
        id: "section-0-table",
        type: ArticleSectionType.TABLE,
        content: {
          headers: ["Real Asset type", "Overview"],
          rows: [
            [
              "Real Estate",
              {
                type: "bullets",
                items: [
                  "Properties across residential, commercial, and industrial sectors",
                  "Returns are driven by optimizing income generation, capital appreciation, and operational efficiency of properties",
                ],
              },
            ],
            [
              "Infrastructure",
              {
                type: "bullets",
                items: [
                  "Assets include utilities, toll roads, and airports",
                  "Returns are driven by stable, long-term cash flows supported by underlying essential services and government contracts related to assets",
                ],
              },
            ],
            [
              "Natural Resources",
              {
                type: "bullets",
                items: [
                  "Assets include commodities, metals, and energy products",
                  "Returns are driven by intrinsic value of raw materials and supply and demand dynamics",
                ],
              },
            ],
          ],
        },
      },
      {
        id: "section-1",
        type: ArticleSectionType.TITLE,
        content: {
          text: "How does Real Asset investing generate returns?",
          level: 5,
        },
      },
      {
        id: "section-1-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Real Asset investing generates returns mainly by income generation and asset appreciation. Income generation is driven by the stable cash flows generated by assets from e.g. rental income, toll revenues, and resource extractions. Asset appreciation is driven by initiatives such as strategic renovations, expansions, or operational improvements of assets.</p>",
        },
      },
      {
        id: "section-2",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Why invest in Real Assets and how do Real Assets fit into a portfolio?",
          level: 5,
        },
      },
      {
        id: "section-2-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Real Asset investing offers several benefits for portfolio diversification and risk mitigation, as Real Assets can offer a way for investors to hedge their portfolios against inflation and receive stable income streams, which tend to be uncorrelated to the economic environment or short-term market fluctuations. These characteristics are further explained and visualized below.</p><p>Inflation hedge: Values of Real Assets tend to increase in line with (and have historically outpaced) inflation, preserving purchasing power and safeguarding portfolios against value erosion in real terms.</p>",
        },
      },
      {
        id: "section-2-2",
        type: ArticleSectionType.CHART,
        content: {
          title: "Average return versus inflation regimes (2001 - 2022)",
          data: [
            {
              category: "Rising",
              naturalResources: 20,
              commodities: 15,
              realAssets: 10,
              globalInfrastructure: 7,
              globalREITs: 0.5,
              euroInflationLinked: 1.5,
              emuEquities: 0.5,
              euroAggBonds: 0,
            },
            {
              category: "Stable",
              naturalResources: 6,
              commodities: 1,
              realAssets: 5,
              globalInfrastructure: 4,
              globalREITs: 16,
              euroInflationLinked: 5.5,
              emuEquities: 5,
              euroAggBonds: 4,
            },
            {
              category: "Declining",
              naturalResources: 1,
              commodities: 0,
              realAssets: 1,
              globalInfrastructure: 6,
              globalREITs: 5.5,
              euroInflationLinked: 5,
              emuEquities: 5,
              euroAggBonds: 6.5,
            },
          ],
          metrics: {
            naturalResources: {
              label: "Natural Resources",
              color: chartColors[0],
              type: "bar",
              yAxisId: "left",
              axisLabel: "Average return (%)",
            },
            commodities: {
              label: "Commodities",
              color: chartColors[1],
              type: "bar",
              yAxisId: "left",
            },
            realAssets: {
              label: "Real Assets",
              color: chartColors[2],
              type: "bar",
              yAxisId: "left",
            },
            globalInfrastructure: {
              label: "Global Infrastructure",
              color: chartColors[3],
              type: "bar",
              yAxisId: "left",
            },
            globalREITs: {
              label: "Global REITs",
              color: chartColors[4],
              type: "bar",
              yAxisId: "left",
            },
            euroInflationLinked: {
              label: "Euro Inflation Linked",
              color: chartColors[5],
              type: "bar",
              yAxisId: "left",
            },
            emuEquities: {
              label: "EMU Equities",
              color: chartColors[6],
              type: "bar",
              yAxisId: "left",
            },
            euroAggBonds: {
              label: "Euro Agg. Bonds",
              color: chartColors[7],
              type: "bar",
              yAxisId: "left",
            },
          },
          footerText: "Source: State Street Global Advisors",
          height: "large",
        },
      },
      {
        id: "section-2-3",
        type: ArticleSectionType.TEXT,
        content: {
          html: '<div class="space-y-4"><p><strong>Stable cash flows:</strong> The income-generating nature of Real Assets provides a stable source of cash flow, offering resilience during economic downturns and market volatility given these assets tend to be part of critical infrastructure.</p><p><strong>Portfolio diversification:</strong> Real Assets exhibit a low correlation with traditional asset classes like stocks and bonds, enhancing portfolio diversification and reducing overall risk exposure.</p></div>',
        },
      },
      {
        id: "section-2-4",
        type: ArticleSectionType.SCATTER_CHART,
        content: {
          icon: createElement(BarChart01_1Icon),
          title:
            "Annualized portfolio volatility and returns (since December 2003)",
          data: [
            {
              id: "portfolio-60-40",
              x: 7.6,
              y: 4.3,
              label: ["60% Bonds", "40% Equities"],
              labelPosition: "bottom",
              labelStyle: "text",
            },
            {
              id: "portfolio-50-30-20",
              x: 7.4,
              y: 4.6,
              label: ["50% Bonds", "30% Equities", "20% Real Assets"],
              labelPosition: "left",
              labelStyle: "text",
            },
            {
              id: "portfolio-50-50",
              x: 9.0,
              y: 4.6,
              label: ["50% Bonds", "50% Equities"],
              labelPosition: "bottom",
              labelStyle: "text",
            },
            {
              id: "portfolio-40-40-20",
              x: 8.8,
              y: 4.9,
              label: ["40% Bonds", "40% Equities", "20% Real Assets"],
              labelPosition: "left",
              labelStyle: "text",
            },
            {
              id: "portfolio-40-60",
              x: 10.4,
              y: 4.9,
              label: ["40% Bonds", "60% Equities"],
              labelPosition: "bottom",
              labelStyle: "text",
            },
            {
              id: "portfolio-30-50-20",
              x: 10.2,
              y: 5.2,
              label: ["30% Bonds", "50% Equities", "20% Real Assets"],
              labelPosition: "right",
              labelStyle: "text",
            },
          ],
          arrows: [
            {
              from: "portfolio-60-40",
              to: "portfolio-50-30-20",
            },
            {
              from: "portfolio-50-50",
              to: "portfolio-40-40-20",
            },
            {
              from: "portfolio-40-60",
              to: "portfolio-30-50-20",
            },
          ],
          xAxisLabel: "Annualized volatility",
          xAxisUnit: "%",
          yAxisUnit: "%",
          xDomain: [7.0, 11.0],
          yDomain: [4.0, 5.4],
          footerText: "Source: State Street Global Advisors",
          height: 280,
        },
      },
    ],
  },
  {
    id: "celestial-3",
    slug: "deep-dive-private-equity",
    title: "Deep Dive: Private Equity",
    description:
      "Best practices for diversifying your investment portfolio to manage risk effectively.",
    previewUrl: "/images/resources/celestial/celestial-article-3.png",
    previewType: PreviewType.Image,
    author: "Emily Thompson",
    publishedDate: "2025-01-01T09:00:00Z",
    readingTime: 10,
    published: true,
    sections: [
      {
        id: "section-1",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Which companies make good targets?",
          level: 5,
        },
      },
      {
        id: "section-2",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Companies with stable and predictable cash flow, alongside robust asset bases generally represent attractive LBO candidates due to their ability to support larger quantities of debt. The consistency in generating stable free cash flow is of high importance as free cash flow is needed to service periodic interest payments and gradually reduce the amount of debt over the investment horizon. A large tangible asset base enhances the accessibility of bank debt, known for its cost-effectiveness in debt financing, by instilling confidence in lenders regarding the potential recovery of principal in the event of bankruptcy.</p>",
        },
      },
      {
        id: "section-3",
        type: ArticleSectionType.CARD_GRID,
        content: {
          title: "Characteristics of a strong LBO candidate",
          items: [
            "Strong cash flow generation",
            "Leading and defensible market position",
            "Growth opportunities",
            "Efficiency enhancement opportunities",
            "Low capital expenditure requirements",
            "Strong asset base",
            "Proven management team",
          ],
        },
      },
      {
        id: "section-4",
        type: ArticleSectionType.TITLE,
        content: {
          text: "How does Private Equity generate returns?",
          level: 5,
        },
      },
      {
        id: "section-5",
        type: ArticleSectionType.TEXT,
        content: {
          html: '<p>Two main ways Private Equity generates returns are debt repayments (i.e. use of leverage) and growth of the target’s value ("enterprise value"). In a traditional LBO, debt typically constitutes 60-70% of the financing structure, while equity makes up the remaining 30-40%. The disproportionately high level of debt incurred by the target is supported by its projected free cash flow and asset base, which enables the sponsor to contribute a small equity investment relative to the purchase price. The ability to leverage the relatively small equity investment is crucial for sponsors to achieve returns. The use of leverage provides the additional benefit of tax savings due to the tax deductibility of interest expense.</p><p>During the period from acquisition until exit ("investment horizon"), cash flow is used predominantly to service and repay the principal amount of debt, thereby growing the fund’s initial equity contribution (Scenario I). At the same time, the sponsor aims to improve the financial performance of the target and grow the existing business. This is achieved through organic growth initiatives and "bolt-on" M&A acquisitions, thereby increasing enterprise value and further enhancing potential returns (Scenario II).</p>',
        },
      },
      {
        id: "section-6",
        type: ArticleSectionType.SCENARIO_RETURNS,
        content: {
          title:
            "Example illustration of two main ways Private Equity generates returns",
          yAxisTicks: [0, 500, 1000, 1500],
          labels: {
            assumptionsTitle: "Assumptions",
            calculationsTitle: "Return calculations",
            equityValueLabel: "Equity value at exit",
            irrLabel: "IRR",
            moicLabel: "MOIC",
            equityLabel: "Equity",
            debtLabel: "Debt",
          },
          scenarios: [
            {
              id: "scenario-debt",
              title: "Scenario I: Debt repayment only",
              bars: [
                {
                  label: "At acquisition",
                  debt: 700,
                  equity: 300,
                },
                {
                  label: "At exit",
                  debt: 200,
                  equity: 800,
                },
              ],
              annotations: [
                {
                  value: 1000,
                  label: "1bn purchase & sales price",
                },
                {
                  value: 500,
                  label: "500m debt repayment",
                },
              ],
              assumptions: [
                { label: "Purchase price", value: "1,000" },
                { label: "Equity contribution (sponsor)", value: "300" },
                { label: "Debt repayment", value: "500" },
                { label: "Sales price at exit", value: "1,000" },
              ],
              calculations: [
                { label: "Equity contribution (sponsor)", value: "300" },
                { label: "Increases to equity value" },
                {
                  label: "Increase in enterprise value",
                  value: "-",
                  indent: true,
                },
                {
                  label: "Decrease in debt from repayment",
                  value: "500",
                  indent: true,
                },
                {
                  label: "Equity value at exit",
                  value: "800",
                  emphasis: true,
                },
              ],
              summary: { irr: "21.7%", moic: "2.7x" },
            },
            {
              id: "scenario-enterprise",
              title: "Scenario II: Enterprise value growth only",
              bars: [
                {
                  label: "At acquisition",
                  debt: 700,
                  equity: 300,
                },
                {
                  label: "At exit",
                  debt: 700,
                  equity: 800,
                },
              ],
              annotations: [
                {
                  value: 1500,
                  label: "1.5bn sales price",
                },
                {
                  value: 1000,
                  label: "1bn purchase price",
                },
                {
                  value: 1250,
                  label: "500m enterprise value growth",
                },
              ],
              assumptions: [
                { label: "Purchase price", value: "1,000" },
                { label: "Equity contribution (sponsor)", value: "300" },
                { label: "Debt repayment", value: "-" },
                { label: "Sales price at exit", value: "1,500" },
              ],
              calculations: [
                { label: "Equity contribution (sponsor)", value: "300" },
                { label: "Increases to equity value" },
                {
                  label: "Increase in enterprise value",
                  value: "500",
                  indent: true,
                },
                {
                  label: "Decrease in debt from repayment",
                  value: "-",
                  indent: true,
                },
                {
                  label: "Equity value at exit",
                  value: "800",
                  emphasis: true,
                },
              ],
              summary: { irr: "21.7%", moic: "2.7x" },
            },
          ],
        },
      },
      {
        id: "section-7",
        type: ArticleSectionType.TITLE,
        content: {
          text: "How does Private Equity exit investments?",
          level: 5,
        },
      },
      {
        id: "section-8",
        type: ArticleSectionType.TEXT,
        content: {
          html: '<p>Most sponsors aim to exit or monetise their investments within a five-year holding period in order to deliver returns to their LPs. These returns are typically realized via a sale to another company ("strategic sale"), a sale to another sponsor, or through an initial public offering ("IPO"), thereby listing the business on a stock exchange ("going public").</p><p>The ultimate decision regarding the timing of the monetization of an investment depends on several factors, including the performance of the target as well as the prevailing market conditions.</p>',
        },
      },
      {
        id: "section-9",
        type: ArticleSectionType.CARD_GRID,
        content: {
          title: "Typical exit routes for Private Equity",
          items: [
            "Strategic sale",
            "Sale to another sponsor",
            "Initial public offering (IPO)",
          ],
        },
      },
    ],
  },
  {
    id: "celestial-4",
    slug: "fund-structure-overview",
    title: "Fund structures overview: semi-liquid and drawdown funds",
    description:
      "Maximize your returns by understanding tax-efficient investment strategies.",
    previewUrl: "/images/resources/celestial/celestial-article-4.png",
    previewType: PreviewType.Image,
    author: "David Park",
    publishedDate: "2024-12-28T11:00:00Z",
    readingTime: 15,
    published: true,
    sections: [
      {
        id: "section-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>Private market funds are usually structured as either semi-liquid or drawdown funds. Semi-liquid funds deploy all capital at the beginning of their lifecycle and typically don't have fixed investment periods. Investors can redeem their shares or withdraw capital at regular intervals, such as on a quarterly or semi-annual basis, as semi-liquid funds offer more frequent and spread-out redemptions over the fund's lifecycle. Drawdown funds have longer lock-up periods and typically don't offer opportunities for investors to sell their investments. These funds require investors to commit capital upfront, however capital is only contributed when called by the GP once a suitable investment opportunity is identified. This structure allows the GP to deploy capital efficiently over time. Distributions to investors are typically made towards the end of the fund's lifecycle, in line with the timing of the fund's divestment phase. This structure aligns more closely with the patient, long-term nature of private equity investments.</p>",
        },
      },
      {
        id: "section-2",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Comparison of key characteristics of semi-liquid and drawdown funds",
          level: 5,
        },
      },
      {
        id: "section-3",
        type: ArticleSectionType.TABLE,
        content: {
          headers: ["Characteristic", "Semi-liquid", "Drawdown"],
          rows: [
            [
              "Liquidity",
              "Investors can redeem capital at set intervals, e.g. monthly or quarterly",
              "Investors can't sell their investments in a fund as their capital is locked up until the end of a fund's lifecycle",
            ],
            [
              "Capital Deployment",
              "Entire capital is deployed at the beginning of a fund's lifecycle",
              "Capital is deployed at various points through a fund's lifecycle, as suitable investment opportunities are identified. Investors commit capital, but only contribute it once it is called by the GP",
            ],
            [
              "Distributions",
              "Distributions are made at regular intervals during a fund's lifecycle",
              "Distributions tend to be made towards the end of a fund's lifecycle when investments are divested by the GP",
            ],
            [
              "Returns",
              "To guarantee liquidity, some assets are held in liquidity sleeves (including cash and public stocks), potentially resulting in lower returns than drawdown funds, which deploy capital more effectively",
              "Returns can be higher than semi-liquid funds as drawdown funds don't need to hold liquidity sleeves to cover redemption requests by investors",
            ],
          ],
        },
      },
      {
        id: "section-4",
        type: ArticleSectionType.CASH_FLOW_PROFILES,
        content: {
          title: "Cash flow profiles of semi-liquid and drawdown funds",
          legend: {
            contributionsLabel: "Contributions",
            distributionsLabel: "Distributions",
          },
          profiles: [
            {
              id: "semi-liquid",
              title: "Semi-liquid fund",
              yAxisTicks: [-100, -50, 0, 50, 100],
              data: [
                { year: "1", contributions: -100, distributions: 10 },
                { year: "2", contributions: 0, distributions: 10 },
                { year: "3", contributions: 0, distributions: 10 },
                { year: "4", contributions: 0, distributions: 10 },
                { year: "5", contributions: 0, distributions: 10 },
                { year: "6", contributions: 0, distributions: 10 },
                { year: "7", contributions: 0, distributions: 10 },
                { year: "8", contributions: 0, distributions: 10 },
                { year: "9", contributions: 0, distributions: 10 },
                { year: "10", contributions: 0, distributions: 10 },
              ],
            },
            {
              id: "drawdown",
              title: "Drawdown fund",
              yAxisTicks: [-30, -15, 0, 15, 30],
              data: [
                { year: "1", contributions: -25, distributions: 0 },
                { year: "2", contributions: -30, distributions: 0 },
                { year: "3", contributions: -30, distributions: 0 },
                { year: "4", contributions: -15, distributions: 0 },
                { year: "5", contributions: 0, distributions: 15 },
                { year: "6", contributions: 0, distributions: 18 },
                { year: "7", contributions: 0, distributions: 25 },
                { year: "8", contributions: 0, distributions: 20 },
                { year: "9", contributions: 0, distributions: 15 },
                { year: "10", contributions: 0, distributions: 5 },
              ],
            },
          ],
        },
      },
    ],
  },
  {
    id: "celestial-5",
    slug: "how-do-private-market-funds-work",
    title: "How do private market funds work and what is the J-curve",
    description:
      "Essential risk management techniques every investor should know.",
    previewUrl: "/images/resources/celestial/celestial-article-1.png",
    previewType: PreviewType.Image,
    author: "Jennifer Martinez",
    publishedDate: "2024-12-25T16:00:00Z",
    readingTime: 7,
    published: true,
    sections: [
      {
        id: "section-1",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Understanding Investment Risk",
          level: 5,
        },
      },
      {
        id: "section-2",
        type: ArticleSectionType.TEXT,
        content: {
          html: '<p>A private market fund is a pool of capital used to invest in private and public companies that fit within a predetermined investment strategy. The fund is managed by a firm that serves as the \'General Partner\' ("GP") of the fund. By contributing capital, investors become \'Limited Partners\' ("LPs") of the fund. As such, the fund is structured as a \'Limited Partnership\'. A GP may manage one or a few funds that typically have different investment criteria such as geography, industry or typical size of each investment. Often GPs periodically raise funds with similar criteria to previous successful funds, creating different "vintages" of a similar fund. The technical life of a fund is called the fund term. Funds are usually structured either as semi-liquid funds (also called "open-ended") or drawdown funds (also called "close-ended"). The most common fund term is ten years, with optional extensions (usually two or three years). The following section focuses on drawdown funds.</p><p>A fund\'s lifecycle typically encompasses three main stages: formation, investment, and harvesting. In the formation stage, which precedes the fund\'s launch, strategies are formulated, fundraising takes place, and initial target companies are identified. During the investment phase, which typically takes place in the first four years of the lifecycle, the fund invests in target companies, issues capital calls to investors, and works with its target companies to improve these companies. In the harvesting stage, the GP gradually divests the target companies and distributes the proceeds from these exits back to the LPs. This stage typically lasts up to six years once the investment phase is completed.</p>',
        },
      },
      {
        id: "section-3",
        type: ArticleSectionType.CASH_FLOW_PROFILES,
        content: {
          legend: {
            contributionsLabel: "Contributions",
            distributionsLabel: "Distributions",
          },
          profiles: [
            {
              id: "drawdown-cashflow",
              showYAxis: false,
              yAxisTicks: [-30, -15, 0, 15, 30],
              data: [
                { year: "Y1", contributions: 25, distributions: 0 },
                { year: "Y2", contributions: 30, distributions: 0 },
                { year: "Y3", contributions: 30, distributions: 0 },
                { year: "Y4", contributions: 15, distributions: 0 },
                { year: "Y5", contributions: 0, distributions: 15 },
                { year: "Y6", contributions: 0, distributions: 18 },
                { year: "Y7", contributions: 0, distributions: 25 },
                { year: "Y8", contributions: 0, distributions: 20 },
                { year: "Y9", contributions: 0, distributions: 15 },
                { year: "Y10", contributions: 0, distributions: 5 },
              ],
            },
          ],
        },
      },
      {
        id: "section-4",
        type: ArticleSectionType.TEXT,
        content: {
          html: '<div class="space-y-4"><p>GPs generally charge both a management fee and a performance fee to LPs. Management fees are charged to cover the fund’s operating costs, whereas performance fees (also called “carried interest” or “carry”) are a share of the profits generated over the fund lifecycle. Performance fees therefore align the interests of GPs and LPs. Typically, management fees consist of 2% of assets under management (“AUM”) and performance fees of 20% of the generated returns. Usually, there is a hurdle rate of return (~8-10%) that LPs must receive before performance fees can be paid to the GP.</p><p>Apollo’s Real Assets business focuses on investment opportunities across its Real Estate, European Principal Finance, Infrastructure, and Sustainable Investing strategies. Apollo targets attractive, value-oriented opportunities across real asset classes, leveraging the expertise of Apollo’s integrated investment platform to transact and drive performance throughout market cycles.</p></div>',
        },
      },
    ],
  },
];
